/**
 * polygonProvider.ts -- MER Algo Footprints v9.2.0
 *
 * Client wrapper for Polygon.io stocks data. Talks to the server-side
 * proxy at /api/polygon/* (server holds the API key).
 *
 * Phase 1 Session B: live fetch + normalize.
 *
 * Boundary:
 *   This file MUST NOT import a Polygon SDK. We call the proxy and
 *   normalize the response shape into our internal Trade schema.
 */

import type { Trade, TradeAggregate } from '../schemas';

const ENDPOINT = '/api/polygon';

/**
 * Raw Polygon /v3/trades result shape (only the fields we use).
 * Documented at https://polygon.io/docs/stocks/get_v3_trades__stockticker
 */
interface PolygonTradeRaw {
  participant_timestamp?: number;     // ns
  sip_timestamp?:         number;     // ns
  price:                  number;
  size:                   number;
  conditions?:            number[];
  exchange?:              number;
  trf_id?:                number;     // present => off-exchange (TRF report)
}

/**
 * Server proxy response shape.
 */
interface ProxyTradesResponse {
  ok:                 boolean;
  symbol?:            string;
  results?:           PolygonTradeRaw[];
  count?:             number;
  limit?:             number;
  partial?:           boolean;
  reason?:            string;
}

/**
 * Result envelope returned by fetchTrades. Carries partial-coverage
 * metadata so downstream engines can degrade confidence appropriately.
 */
export interface TradesFetchResult {
  trades:  Trade[];
  partial: boolean;
}

/**
 * Normalize a single Polygon trade to our internal Trade schema.
 * Returns null if the trade is missing required fields (filtered out).
 */
function normalizeTrade(raw: PolygonTradeRaw, symbol: string): Trade | null {
  if (typeof raw.price !== 'number' || typeof raw.size !== 'number') return null;
  if (!(raw.price > 0) || !(raw.size > 0)) return null;

  // Prefer participant timestamp (when the trade happened) over sip
  // (when it was disseminated). Both are ns -> convert to ms.
  const tsNs = raw.participant_timestamp ?? raw.sip_timestamp;
  if (typeof tsNs !== 'number' || !Number.isFinite(tsNs)) return null;
  const timestamp = Math.floor(tsNs / 1_000_000);

  // Off-exchange signal: presence of trf_id is the primary indicator.
  // Polygon emits trf_id on trades reported through a Trade Reporting
  // Facility (TRF), which is the standard off-exchange / dark pool route.
  const isOffExchange = typeof raw.trf_id === 'number';

  // venue: use trf_id if off-exchange, else the lit exchange code.
  const venue = isOffExchange
    ? `TRF:${raw.trf_id}`
    : (typeof raw.exchange === 'number' ? `EX:${raw.exchange}` : 'UNKNOWN');

  return {
    symbol,
    timestamp,
    price:         raw.price,
    size:          raw.size,
    venue,
    conditions:    Array.isArray(raw.conditions) ? raw.conditions : [],
    isOffExchange,
  };
}

/**
 * Fetch recent trades for a symbol within a time window.
 *
 * Returns null on any failure (network, 401, 5xx, 503 not-configured).
 * Engines should treat null as "no data available, skip this cycle".
 *
 * @param symbol  Ticker (will be uppercased server-side)
 * @param fromMs  Window start, Unix ms
 * @param toMs    Window end, Unix ms (exclusive)
 */
export async function fetchTrades(
  symbol: string,
  fromMs: number,
  toMs:   number,
): Promise<TradesFetchResult | null> {
  if (!symbol || !Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null;
  if (fromMs >= toMs) return null;

  const url = new URL(`${ENDPOINT}/trades/${encodeURIComponent(symbol)}`, window.location.origin);
  url.searchParams.set('from', String(fromMs));
  url.searchParams.set('to',   String(toMs));

  try {
    const r = await fetch(url.toString(), { credentials: 'include' });
    if (!r.ok) return null;
    const data = (await r.json()) as ProxyTradesResponse;
    if (!data || !data.ok || !Array.isArray(data.results)) return null;

    const sym = (data.symbol || symbol).toUpperCase();
    const trades: Trade[] = [];
    for (const raw of data.results) {
      const t = normalizeTrade(raw, sym);
      if (t) trades.push(t);
    }
    return { trades, partial: Boolean(data.partial) };
  } catch {
    return null;
  }
}

/**
 * Fetch aggregated trade stats for a symbol within a time window.
 * Computed client-side from fetchTrades. May move server-side later
 * if call volume justifies it.
 */
export async function fetchAggregate(
  symbol: string,
  fromMs: number,
  toMs:   number,
): Promise<TradeAggregate | null> {
  const result = await fetchTrades(symbol, fromMs, toMs);
  if (result === null) return null;
  const trades = result.trades;

  let totalVolume       = 0;
  let offExchangeVolume = 0;
  let offExchangeCount  = 0;
  let largestBlockSize  = 0;

  for (const t of trades) {
    totalVolume += t.size;
    if (t.isOffExchange) {
      offExchangeVolume += t.size;
      offExchangeCount  += 1;
    }
    if (t.size > largestBlockSize) largestBlockSize = t.size;
  }

  return {
    symbol:           symbol.toUpperCase(),
    windowStart:      fromMs,
    windowEnd:        toMs,
    totalVolume,
    offExchangeVolume,
    offExchangePct:   totalVolume > 0 ? offExchangeVolume / totalVolume : 0,
    largestBlockSize,
    totalTradeCount:  trades.length,
    offExchangeCount,
  };
}
